/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { UserOutlined } from '@ant-design/icons'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { GENDER_OPTIONS } from '@sportninja/common/features/profile/player'
import PlayerAvatar from '../PlayerAvatar'
import PlayerIdChip from '../PlayerIdChip'
import { useEditPlayerContext } from '../../EditPlayerContext'
import EditableSection from '../EditableSection'
import {
  TextField,
  NumberField,
  DateOfBirthField,
  SelectField,
  PhoneField,
  FieldRow,
} from '../FormikField'

interface PersonalSectionProps {
  /**
   * Mark First/Last Name as required (shows `*`). Used by the roster Add/Edit
   * Player flow where names are mandatory; the profile Edit Player modal leaves
   * this false (names optional there).
   */
  requiredNameFields?: boolean
  /**
   * Mark Email as required (shows `*`). Driven by the roster's `require_email`
   * flag; the profile Edit Player modal leaves this false (email optional).
   */
  requireEmail?: boolean
  /**
   * Roster add-existing (copy) mode: the player already exists, so show only
   * First/Last name + Email read-only and hide the other personal fields (the
   * BE keeps their profile). Defaults false (full editable section).
   */
  readOnlyExisting?: boolean
  /**
   * Lock the Email field (read-only) when editing an existing player — the
   * email is the player's identity and can't be changed here. Add-new flows
   * leave this false so the email can be typed. (`readOnlyExisting` already
   * locks email for add-existing/copy, so this is independent of it.)
   */
  lockEmail?: boolean
  /**
   * Lock the First/Last Name fields (read-only) when editing an existing
   * player — like `lockEmail`, these identify the player and can't be changed
   * here. Add-new flows leave this false so names can be typed.
   */
  lockNames?: boolean
  /**
   * Helper copy rendered under the Email field. Used by the roster Add/Edit
   * Player flow to warn that filling in an email sends the player an invite.
   * Omitted elsewhere (the profile Edit Player modal sends no invite).
   */
  emailHelperText?: string
  /**
   * Colour of the email helper text. Defaults to the muted hint colour; pass a
   * token to draw attention to a consequence (see `TextField`).
   */
  emailHelperTextColor?: string
  /**
   * Extra read-only id chips stacked beneath the PLAYER ID chip (e.g. the Canlan
   * SALESFORCE ID). Supplied by the caller because the id lives in that flow's
   * form — the profile form has no `salesforceId`, so this section can't read it
   * directly.
   */
  extraIdChips?: React.ReactNode
}

const PersonalSection: React.FC<PersonalSectionProps> = ({
  requiredNameFields = false,
  requireEmail = false,
  readOnlyExisting = false,
  lockEmail = false,
  lockNames = false,
  emailHelperText,
  emailHelperTextColor,
  extraIdChips = null,
}) => {
  const { playerId } = useEditPlayerContext()

  // Chips stack top-down: PLAYER ID first, then any caller-supplied ids under it.
  // Either can be absent (no playerId on create; no extra chips outside Canlan).
  const idChips =
    playerId || extraIdChips ? (
      <div css={idChipStackStyle}>
        {playerId ? <PlayerIdChip playerId={playerId} /> : null}
        {extraIdChips}
      </div>
    ) : null

  return (
    <EditableSection
      id='personal'
      title={capitalizeWords('Personal Information')}
      icon={<UserOutlined />}
      titleAccessory={idChips}
      // The photo is part of this section, so it changes only while the section
      // is edited; the Edit / Save actions share its row (Player Details'
      // Contact layout). Without read mode (Add Player) it renders alone.
      renderLead={(actions, isReadMode) => {
        const avatar = (
          <PlayerAvatar readOnly={readOnlyExisting || isReadMode} />
        )
        return actions ? (
          <div css={leadStyle}>
            {avatar}
            {actions}
          </div>
        ) : (
          avatar
        )
      }}
    >
      <FieldRow cols={2}>
        <TextField
          name='firstName'
          label='First Name'
          placeholder={capitalizeWords('First name')}
          required={requiredNameFields}
          disabled={readOnlyExisting || lockNames}
        />
        <TextField
          name='lastName'
          label='Last Name'
          placeholder={capitalizeWords('Last name')}
          required={requiredNameFields}
          disabled={readOnlyExisting || lockNames}
        />
      </FieldRow>
      {/* Add-existing: only name + email are shown (read-only); the BE keeps the
          player's own profile, so the rest of the personal fields are hidden. */}
      {!readOnlyExisting ? (
        <FieldRow cols={2} collapseAt={640}>
          <DateOfBirthField name='birthDate' label='Date of Birth' />
          <SelectField
            name='genderId'
            label='Gender'
            options={GENDER_OPTIONS as Array<{ label: string; value: string }>}
            placeholder={capitalizeWords('Select...')}
          />
        </FieldRow>
      ) : null}
      {/* Phone Number | Email — moved up from the (removed) Contact section. In
          add-existing only Email is shown (read-only). */}
      <FieldRow cols={2}>
        <TextField
          name='email'
          label='Email'
          placeholder='name@example.com'
          required={requireEmail}
          disabled={readOnlyExisting || lockEmail}
          helperText={emailHelperText}
          helperTextColor={emailHelperTextColor}
        />
        {!readOnlyExisting ? (
          <PhoneField name='phoneNumber' label='Phone Number' />
        ) : null}
      </FieldRow>
      {!readOnlyExisting ? (
        <FieldRow cols={2} collapseAt={640}>
          <FieldRow cols={2}>
            <NumberField
              name='heightFt'
              label='Height (ft)'
              addonAfter='ft'
              placeholder='0'
            />
            <NumberField
              name='heightIn'
              label='Height (in)'
              addonAfter='in'
              placeholder='0'
            />
          </FieldRow>
          <NumberField
            name='weight'
            label='Weight'
            addonAfter='lbs'
            placeholder='0'
          />
        </FieldRow>
      ) : null}
    </EditableSection>
  )
}

export default PersonalSection

// ─── Styles ────────────────────────────────────────────────────────────────

// Stacks the id chips vertically, each hugging the right edge so their copy
// buttons line up under one another.
const idChipStackStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

// The photo on the left, the section's Edit / Save actions on the right.
const leadStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`
